/**
 * Display / export formatting for values read from an Arrow JS `Table`
 * (apache-arrow 21). What `Vector.get(i)` hands back per type — verified
 * against pyarrow-produced IPC, not guessed:
 *
 *   Int8..Int32, UInt8..UInt32, Float*   number
 *   Int64 / UInt64                        bigint
 *   Decimal                               DecimalBigNum — `.toString()` is the
 *                                         UNSCALED integer ("123456" for 1234.56
 *                                         at scale 2, "-1" for -0.01); scale/
 *                                         precision live on the column type
 *   Timestamp (any unit, tz or not)       number — epoch MILLISECONDS, with the
 *                                         sub-ms part as a fraction (…123.456)
 *   Date32 / Date64                       number — epoch ms at UTC midnight
 *   Time32 / Time64                       number / bigint in the type's unit
 *   Bool                                  boolean
 *   Utf8 / LargeUtf8                      string
 *   Binary / FixedSizeBinary              Uint8Array
 *   List / FixedSizeList                  Vector (get(i) per element)
 *   Struct                                StructRow (field access by name)
 *   Map                                   MapRow
 *   Null                                  null
 *   Dictionary                            the decoded value (use type.dictionary)
 */
import { DataType, Type, type Vector } from 'apache-arrow';

const pad = (n: number, width: number) => String(n).padStart(width, '0');

/** Seconds-fraction digits for a time-like unit (0=s, 1=ms, 2=us, 3=ns). */
const unitDigits = (unit: number) => [0, 3, 6, 9][unit] ?? 6;

/** Arrow decimal → plain decimal string ("1234.56"), from the unscaled bigint. */
export const formatDecimal = (value: unknown, scale: number): string => {
  const unscaled = BigInt(String(value));
  const negative = unscaled < 0n;
  let digits = (negative ? -unscaled : unscaled).toString();
  if (scale <= 0) return (negative ? '-' : '') + digits + '0'.repeat(-scale);
  digits = digits.padStart(scale + 1, '0');
  const intPart = digits.slice(0, digits.length - scale);
  const fracPart = digits.slice(digits.length - scale);
  return `${negative ? '-' : ''}${intPart}.${fracPart}`;
};

/** Epoch-ms (with optional fractional sub-ms) → "YYYY-MM-DD HH:MM:SS[.ffffff]" in UTC. */
export const formatTimestampMs = (ms: number, unit: number, timezone: string | null | undefined): string => {
  const whole = Math.floor(ms);
  const d = new Date(whole);
  if (Number.isNaN(d.getTime())) return String(ms);
  const base =
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1, 2)}-${pad(d.getUTCDate(), 2)} ` +
    `${pad(d.getUTCHours(), 2)}:${pad(d.getUTCMinutes(), 2)}:${pad(d.getUTCSeconds(), 2)}`;
  const digits = unitDigits(unit);
  let frac = '';
  if (digits > 0) {
    // millis from the Date, then the sub-ms fraction Arrow JS left on the number.
    const subMs = ms - whole; // 0 ≤ subMs < 1
    const fracAll = pad(d.getUTCMilliseconds(), 3) + Math.round(subMs * 1_000_000).toString().padStart(6, '0');
    frac = '.' + fracAll.slice(0, digits);
  }
  return base + frac + (timezone ? ' UTC' : '');
};

export const formatDateMs = (ms: number): string => {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return String(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1, 2)}-${pad(d.getUTCDate(), 2)}`;
};

/** Time-of-day in the type's unit → "HH:MM:SS[.fff…]". */
export const formatTimeOfDay = (value: number | bigint, unit: number): string => {
  const perSecond = [1n, 1_000n, 1_000_000n, 1_000_000_000n][unit] ?? 1_000_000n;
  const v = BigInt(value);
  const secs = v / perSecond;
  const frac = v % perSecond;
  const h = secs / 3600n, m = (secs % 3600n) / 60n, s = secs % 60n;
  const digits = unitDigits(unit);
  const fracStr = digits > 0 ? '.' + frac.toString().padStart(digits, '0') : '';
  return `${pad(Number(h), 2)}:${pad(Number(m), 2)}:${pad(Number(s), 2)}${fracStr}`;
};

const bytesToHex = (bytes: Uint8Array): string =>
  '0x' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

const bytesToBase64 = (bytes: Uint8Array): string => {
  let bin = '';
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin);
};

/** bigint → number when it fits exactly, else a string (JSON has no bigint). */
const jsonSafeBigInt = (v: bigint): number | string =>
  v <= BigInt(Number.MAX_SAFE_INTEGER) && v >= BigInt(Number.MIN_SAFE_INTEGER) ? Number(v) : v.toString();

/**
 * Convert an Arrow cell to a plain JSON-serialisable value: numbers stay
 * numbers, decimals/timestamps/dates/times become strings, binary → base64,
 * lists → arrays, structs/maps → objects. Recurses with the child types so
 * nested decimals/timestamps format correctly too.
 */
export const arrowValueToJson = (value: unknown, type: DataType): unknown => {
  if (value === null || value === undefined) return null;
  if (DataType.isDictionary(type)) return arrowValueToJson(value, type.dictionary);
  switch (type.typeId) {
    case Type.Null: return null;
    case Type.Bool: return Boolean(value);
    case Type.Int:
      return typeof value === 'bigint' ? jsonSafeBigInt(value) : value;
    case Type.Float: return value;
    case Type.Decimal: return formatDecimal(value, (type as unknown as { scale: number }).scale);
    case Type.Timestamp: {
      const t = type as unknown as { unit: number; timezone?: string | null };
      return formatTimestampMs(Number(value), t.unit, t.timezone);
    }
    case Type.Date: return formatDateMs(Number(value));
    case Type.Time: return formatTimeOfDay(value as number | bigint, (type as unknown as { unit: number }).unit);
    case Type.Utf8:
    case Type.LargeUtf8: return String(value);
    case Type.Binary:
    case Type.LargeBinary:
    case Type.FixedSizeBinary: return bytesToBase64(value as Uint8Array);
    case Type.List:
    case Type.LargeList:
    case Type.FixedSizeList: {
      const vec = value as Vector;
      const child = type.children?.[0]?.type as DataType | undefined;
      const out: unknown[] = [];
      for (let i = 0; i < vec.length; i++) out.push(child ? arrowValueToJson(vec.get(i), child) : vec.get(i));
      return out;
    }
    case Type.Struct: {
      const row = value as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const f of type.children ?? []) out[f.name] = arrowValueToJson(row[f.name], f.type);
      return out;
    }
    default: {
      // Map, Interval, Duration, Union…: fall back to Arrow's own toJSON.
      const anyVal = value as { toJSON?: () => unknown };
      const raw = typeof anyVal?.toJSON === 'function' ? anyVal.toJSON() : value;
      return JSON.parse(JSON.stringify(raw, (_k, v) => (typeof v === 'bigint' ? jsonSafeBigInt(v) : v)));
    }
  }
};

/** Cell text for the grid, clipboard and CSV/TSV. NULL → "NULL" (callers may substitute). */
export const formatArrowValue = (value: unknown, type: DataType): string => {
  if (value === null || value === undefined) return 'NULL';
  if (DataType.isDictionary(type)) return formatArrowValue(value, type.dictionary);
  switch (type.typeId) {
    case Type.Bool: return value ? 'true' : 'false';
    case Type.Int: return String(value);
    case Type.Float: return String(value);
    case Type.Decimal: return formatDecimal(value, (type as unknown as { scale: number }).scale);
    case Type.Timestamp: {
      const t = type as unknown as { unit: number; timezone?: string | null };
      return formatTimestampMs(Number(value), t.unit, t.timezone);
    }
    case Type.Date: return formatDateMs(Number(value));
    case Type.Time: return formatTimeOfDay(value as number | bigint, (type as unknown as { unit: number }).unit);
    case Type.Utf8:
    case Type.LargeUtf8: return String(value);
    case Type.Binary:
    case Type.LargeBinary:
    case Type.FixedSizeBinary: return bytesToHex(value as Uint8Array);
    default: return JSON.stringify(arrowValueToJson(value, type));
  }
};

/** Right-align these in the grid. */
export const isNumericType = (type: DataType): boolean =>
  DataType.isInt(type) || DataType.isFloat(type) || DataType.isDecimal(type);

/** Sort comparator on RAW Arrow values of one column; nulls sort last. */
export const compareArrowValues = (a: unknown, b: unknown, type: DataType): number => {
  const aNull = a === null || a === undefined, bNull = b === null || b === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (DataType.isDictionary(type)) return compareArrowValues(a, b, type.dictionary);
  switch (type.typeId) {
    case Type.Int:
    case Type.Float:
    case Type.Timestamp:
    case Type.Date:
    case Type.Time: {
      const x = a as number | bigint, y = b as number | bigint;
      return x < y ? -1 : x > y ? 1 : 0;
    }
    case Type.Decimal: {
      const x = BigInt(String(a)), y = BigInt(String(b));
      return x < y ? -1 : x > y ? 1 : 0;
    }
    case Type.Bool: return Number(a) - Number(b);
    case Type.Utf8:
    case Type.LargeUtf8: return String(a).localeCompare(String(b));
    default: return formatArrowValue(a, type).localeCompare(formatArrowValue(b, type));
  }
};
